"use client";

import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import StrongSymbolRow from "./StrongSymbolRow";
import { sortSectorObj } from "./util-sort";
import { useStore } from "../services/store";
import { isRealETF } from "./symbols";
import { formatPattern } from "./util-filter";
import {
  groupSymbolsBySector,
  sortGroupsByStrength,
  sortGroupsBySector,
  sortSectorSymbolsByPercentChange,
  filterSymbolsByPercentageThreshold,
  filterIdenticalSymbolGroups,
} from "./util-symbol-grouping";

export const TAB_ALL = "ALL";
const COUNT_THRESHOLDS = {
  VV: 8,
  V: 20,
  SS: 10,
  S: 20,
};
const COUNT_HIGHLIGHT = "#fff9c4";

const hasERInfo = (symbol, erMap) => {
  const sym = (symbol || "").toString();
  const er =
    erMap?.[sym] ?? erMap?.[sym.toLowerCase()] ?? erMap?.[sym.toUpperCase()];

  return er !== undefined && er !== null && String(er).trim() !== "";
};

const filterSymbolsByERInfo = (symbolEntries, erMap) => {
  return symbolEntries.filter(([symbol]) => hasERInfo(symbol, erMap));
};

export function getStrongCounts(obj) {
  const entries = Object.entries(obj || {});
  const counts = {
    SS: entries.filter(([, v]) => (v?.pattern || "").includes("SS")).length,
    S: entries.filter(([, v]) => (v?.pattern || "").includes("S")).length,
    VV: entries.filter(([, v]) => (v?.pattern || "").includes("VV")).length,
    V: entries.filter(([, v]) => (v?.pattern || "").includes("V")).length,
    ALL: entries.length,
  };

  return counts;
}

export function StrongTabs({ tab, onChange, counts }) {
  const TabButton = ({ k, label }) => {
    const active = tab === k;
    const n = counts?.[k] ?? 0;
    const threshold = COUNT_THRESHOLDS[k];
    const overThreshold = Number.isFinite(threshold) && n > threshold;

    return (
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();

          if (onChange) onChange(k);
        }}
        onKeyDown={(e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          e.stopPropagation();

          if (onChange) onChange(k);
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 8px",
          borderRadius: 999,
          cursor: "pointer",
          userSelect: "none",
          border: active
            ? "1px solid rgba(255,255,255,.35)"
            : "1px solid rgba(255,255,255,.12)",
          background: active ? "rgba(255,255,255,.10)" : "transparent",
          color: active ? "rgba(255,255,255,.95)" : "rgba(255,255,255,.70)",
          fontSize: 12,
        }}
        title={`${label}: ${n}`}
      >
        <span>{label}</span>
        <span
          style={{
            minWidth: 18,
            padding: "0 6px",
            height: 16,
            borderRadius: 999,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            lineHeight: 1,
            background: overThreshold
              ? "rgba(255, 249, 196, 0.2)"
              : "rgba(255,255,255,.12)",
            color: overThreshold ? COUNT_HIGHLIGHT : "rgba(255,255,255,.85)",
          }}
        >
          {n}
        </span>
      </span>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        marginBottom: 8,
        marginLeft: 2,
      }}
    >
      <TabButton k={TAB_ALL} label="All" />
      <TabButton k="SS" label="SS" />
      <TabButton k="S" label="S" />
      <TabButton k="VV" label="VV" />
      <TabButton k="V" label="V" />
    </div>
  );
}

export default observer(function StrongBlock({
  obj,
  isPos,
  colCh = 10,
  tab,
  onTabChange,
  showTabs = true,
}) {
  const store = useStore();
  const [localTab, setLocalTab] = useState(TAB_ALL);
  const activeTab = tab ?? localTab;
  const setActiveTab = onTabChange ?? setLocalTab;

  const rawEntries = useMemo(() => Object.entries(obj || {}), [obj]);

  // Apply percentage filter if enabled
  const entries = useMemo(() => {
    const percentageFilteredEntries = store.filterByPercentageThreshold
      ? filterSymbolsByPercentageThreshold(
          rawEntries,
          store.currentGridMap,
          store.formattedDate,
          5,
        )
      : rawEntries;

    return store.showEROnly
      ? filterSymbolsByERInfo(percentageFilteredEntries, store.erMap)
      : percentageFilteredEntries;
  }, [
    rawEntries,
    store.filterByPercentageThreshold,
    store.currentGridMap,
    store.formattedDate,
    store.showEROnly,
    store.erMap,
  ]);

  const symbols = useMemo(() => Object.keys(obj || {}), [obj]);

  // fetch relevant symbols (refetch when obj/date changes)
  useEffect(() => {
    if (symbols.length) {
      store.fetchStrongSymbols({ symbols, date: store.formattedDate });
    }
  }, [store, store.formattedDate, symbols]);

  // ---------- sorting logic ----------
  const sortEntries = (list) => {
    if (!store.strongSort) {
      return list.sort((a, b) => {
        const sectorCompare = sortSectorObj(
          { sector: a[1].sector },
          { sector: b[1].sector },
        );
        if (sectorCompare !== 0) return sectorCompare;

        const isAETF = isRealETF(a[0]);
        const isBETF = isRealETF(b[0]);

        if (isAETF && !isBETF) return -1;
        if (!isAETF && isBETF) return 1;

        const marketCapA = store.marketCapMap?.[a[0]] ?? 0;
        const marketCapB = store.marketCapMap?.[b[0]] ?? 0;

        return marketCapB - marketCapA;
      });
    }

    // strongSort === true
    if (store.strongSort) {
      return list.sort((a, b) => {
        const sA = a[0].toLowerCase();
        const sB = b[0].toLowerCase();

        const ticksA =
          store.currentGridMap?.[sA] || store.currentGridMap?.[a[0]] || [];
        const ticksB =
          store.currentGridMap?.[sB] || store.currentGridMap?.[b[0]] || [];

        const [todayA, prevA] = ticksA;
        const [todayB, prevB] = ticksB;

        const pctA =
          todayA && prevA && Number(prevA.close)
            ? ((+todayA.close - +prevA.close) / +prevA.close) * 100
            : -Infinity;

        const pctB =
          todayB && prevB && Number(prevB.close)
            ? ((+todayB.close - +prevB.close) / +prevB.close) * 100
            : -Infinity;

        return isPos ? pctB - pctA : pctA - pctB;
        /*
        if (pctA !== pctB) {
          // For positive moves: descending order (largest positive first)
          // For negative moves: ascending order (most negative first)
          return isPos ? pctB - pctA : pctA - pctB;
        }

        
        const mcA = store.gridMap?.[a[0]]?.marketCap ?? 0;
        const mcB = store.gridMap?.[b[0]]?.marketCap ?? 0;

        return mcB - mcA;
        */
      });
    }

    return list.sort((a, b) => {
      const pa = formatPattern(a[1]?.pattern || "");
      const pb = formatPattern(b[1]?.pattern || "");

      const priority = ["SS", "S", "VV", "V", "s", "v"];

      for (const token of priority) {
        const aHas = pa.includes(token);
        const bHas = pb.includes(token);
        if (aHas !== bHas) return bHas ? 1 : -1;
      }

      if (pb.length !== pa.length) return pb.length - pa.length;

      const marketCapA = store.gridMap?.[a[0]]?.marketCap ?? 0;
      const marketCapB = store.gridMap?.[b[0]]?.marketCap ?? 0;

      return marketCapB - marketCapA;
    });
  };

  // ---------- groups + counts (ALWAYS computed, even if empty) ----------
  const { groups, counts } = useMemo(() => {
    const g = {
      SS: entries.filter(([, v]) => (v?.pattern || "").includes("SS")),
      VV: entries.filter(([, v]) => (v?.pattern || "").includes("VV")),
      // avoid double counting SS in S
      S: entries.filter(([, v]) => {
        const p = v?.pattern || "";
        return p.includes("S");
      }),
      // avoid double counting VV in V
      V: entries.filter(([, v]) => {
        const p = v?.pattern || "";
        return p.includes("V");
      }),
      ALL: entries,
    };

    const c = {
      SS: g.SS.length,
      S: g.S.length,
      VV: g.VV.length,
      V: g.V.length,
      ALL: entries.length,
    };

    return { groups: g, counts: c };
  }, [entries]);

  // ---------- sorted lists (ALWAYS computed) ----------
  const sortedSS = useMemo(
    () => sortEntries([...groups.SS]),
    [groups.SS, store.strongSort, store.isPercent, store.formattedDate],
  );
  const sortedS = useMemo(
    () => sortEntries([...groups.S]),
    [groups.S, store.strongSort, store.isPercent, store.formattedDate],
  );
  const sortedVV = useMemo(
    () => sortEntries([...groups.VV]),
    [groups.VV, store.strongSort, store.isPercent, store.formattedDate],
  );
  const sortedV = useMemo(
    () => sortEntries([...groups.V]),
    [groups.V, store.strongSort, store.isPercent, store.formattedDate],
  );

  // ---------- tab visibility ----------
  const showSS = activeTab === TAB_ALL || activeTab === "SS";
  const showS = activeTab === TAB_ALL || activeTab === "S";
  const showVV = activeTab === TAB_ALL || activeTab === "VV";
  const showV = activeTab === TAB_ALL || activeTab === "V";

  const Divider = ({ show }) =>
    show ? (
      <div
        className="mv3"
        style={{
          height: 8,
          marginLeft: 6,
          marginRight: 6,
          borderRadius: 4,
          background:
            "linear-gradient(to right, rgba(255,255,255,0.3), rgba(255,255,255,0.08), rgba(255,255,255,0.3))",
        }}
      />
    ) : null;

  // ✅ now it’s safe to early return (hooks already ran)
  if (!entries.length) {
    return (
      <div
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 12,
          color: "#9ca3af",
        }}
      >
        (no strong ticks)
      </div>
    );
  }

  return (
    <div
      className="font-mono text-sm whitespace-pre leading-5 near-white bg-near-black"
      style={{
        marginBottom: 8,
        borderLeft: `3px solid ${
          isPos ? "rgba(34,197,94,.9)" : "rgba(239,68,68,.9)"
        }`,
        padding: 8,
        paddingLeft: 12,
        borderTopLeftRadius: 8,
        borderBottomLeftRadius: 8,
        backgroundColor: isPos ? "rgba(20,83,45,.3)" : "rgba(76,29,29,.3)",
        boxShadow: "0 2px 6px rgba(0,0,0,.25)",
      }}
    >
      {store.sectorSymbolsSortByStrength ? (
        // When sectorSymbolsSortByStrength is true, group by sector (like KVRow)
        (() => {
          // Apply tab filtering first, then group by sector
          let filteredEntries = entries;
          if (activeTab !== TAB_ALL) {
            if (activeTab === "SS") {
              filteredEntries = entries.filter(([, v]) =>
                (v?.pattern || "").includes("SS"),
              );
            } else if (activeTab === "VV") {
              filteredEntries = entries.filter(([, v]) =>
                (v?.pattern || "").includes("VV"),
              );
            } else if (activeTab === "S") {
              filteredEntries = entries.filter(([, v]) =>
                (v?.pattern || "").includes("S"),
              );
            } else if (activeTab === "V") {
              filteredEntries = entries.filter(([, v]) =>
                (v?.pattern || "").includes("V"),
              );
            }
          }

          const symbolEntries = filteredEntries;
          const grouped = groupSymbolsBySector(symbolEntries);
          const sortedGroupEntries = store.sectorSymbolsSortBySector
            ? sortGroupsByStrength(
                grouped,
                store.currentGridMap,
                store.formattedDate,
                isPos,
              )
            : sortGroupsBySector(
                grouped,
                store.currentGridMap,
                store.formattedDate,
                isPos,
              );
          const groupEntries = filterIdenticalSymbolGroups(sortedGroupEntries);

          return groupEntries.map(([sectorKey, sectorSymbols], groupIndex) => (
            <div key={sectorKey}>
              {groupIndex > 0 && <div style={{ height: 8 }} />}
              {/* Group name header */}
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.6)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: 4,
                  paddingLeft: 4,
                  borderLeft: "2px solid rgba(255,255,255,0.2)",
                }}
              >
                {sectorKey}
              </div>
              {(() => {
                // Sort symbols within group by percentage change if isPercent and strongSort, otherwise keep original order
                const orderedSymbols = store.strongSort
                  ? sortSectorSymbolsByPercentChange(
                      sectorSymbols,
                      store.currentGridMap,
                      store.formattedDate,
                      isPos, // isPos determines sort direction
                    )
                  : sectorSymbols;

                return orderedSymbols.map(([sym, val]) => (
                  <StrongSymbolRow
                    key={sym}
                    sym={sym}
                    val={val}
                    isPos={isPos}
                    colCh={colCh}
                  />
                ));
              })()}
            </div>
          ));
        })()
      ) : store.strongSort ? (
        // When strongSort is true, show flat sorted list by percentage change
        sortEntries([...entries]).map(([sym, val]) => (
          <StrongSymbolRow
            key={sym}
            sym={sym}
            val={val}
            isPos={isPos}
            colCh={colCh}
          />
        ))
      ) : (
        <>
          {/* Tabs */}
          {showTabs && (
            <StrongTabs
              tab={activeTab}
              onChange={setActiveTab}
              counts={counts}
            />
          )}

          {/* SS */}
          {showSS && sortedSS.length > 0 && (
            <>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.6)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: 4,
                  paddingLeft: 4,
                  borderLeft: "2px solid rgba(255,255,255,0.2)",
                }}
              >
                SS
              </div>
              {sortedSS.map(([sym, val]) => (
                <StrongSymbolRow
                  key={`SS-${sym}`}
                  sym={sym}
                  val={val}
                  isPos={isPos}
                  colCh={colCh}
                />
              ))}
            </>
          )}

          <Divider
            show={showSS && showS && sortedSS.length > 0 && sortedS.length > 0}
          />

          {/* S */}
          {showS && sortedS.length > 0 && (
            <>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.6)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: 4,
                  paddingLeft: 4,
                  borderLeft: "2px solid rgba(255,255,255,0.2)",
                }}
              >
                S
              </div>
              {sortedS.map(([sym, val]) => (
                <StrongSymbolRow
                  key={`S-${sym}`}
                  sym={sym}
                  val={val}
                  isPos={isPos}
                  colCh={colCh}
                />
              ))}
            </>
          )}

          <Divider
            show={showS && showVV && sortedS.length > 0 && sortedVV.length > 0}
          />

          {/* VV */}
          {showVV && sortedVV.length > 0 && (
            <>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.6)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: 4,
                  paddingLeft: 4,
                  borderLeft: "2px solid rgba(255,255,255,0.2)",
                }}
              >
                VV
              </div>
              {sortedVV.map(([sym, val]) => (
                <StrongSymbolRow
                  key={`VV-${sym}`}
                  sym={sym}
                  val={val}
                  isPos={isPos}
                  colCh={colCh}
                />
              ))}
            </>
          )}

          <Divider
            show={showVV && showV && sortedVV.length > 0 && sortedV.length > 0}
          />

          {/* V */}
          {showV && sortedV.length > 0 && (
            <>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.6)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: 4,
                  paddingLeft: 4,
                  borderLeft: "2px solid rgba(255,255,255,0.2)",
                }}
              >
                V
              </div>
              {sortedV.map(([sym, val]) => (
                <StrongSymbolRow
                  key={`V-${sym}`}
                  sym={sym}
                  val={val}
                  isPos={isPos}
                  colCh={colCh}
                />
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
});
